import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { unlockedTeamSlots } from '@fakerpg/core';
import { useSnapshot, useT } from '../game/context.js';
import { play } from './sfx.js';
import { Portal } from './Portal.js';

/** In-game setting OR the OS preference; environments without matchMedia (tests) count as reduced. */
export function useReducedMotion(): boolean {
  const setting = useSnapshot().prefs.reducedMotion;
  const [media, setMedia] = useState(() => (typeof window.matchMedia !== 'function' ? true : window.matchMedia('(prefers-reduced-motion: reduce)').matches));
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const on = () => setMedia(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return setting || media;
}

/** Animated number: eases from the previously shown value to the new one. */
export function CountUp({ value, format = (n) => String(n), duration = 650 }: { value: number; format?: (n: number) => string; duration?: number }) {
  const reduced = useReducedMotion();
  const [shown, setShown] = useState(value);
  const shownRef = useRef(value);
  useEffect(() => {
    const from = shownRef.current;
    if (reduced || from === value || typeof requestAnimationFrame !== 'function') {
      shownRef.current = value;
      setShown(value);
      return;
    }
    const start = performance.now();
    let raf = 0;
    const step = (t: number) => {
      const k = Math.min(1, (t - start) / duration);
      const v = from + (value - from) * (1 - Math.pow(1 - k, 3));
      shownRef.current = v;
      setShown(v);
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, reduced, duration]);
  return <span className="countup">{format(Math.round(shown))}</span>;
}

/** One-shot particle burst; change `trigger` to replay. */
export function Burst({ color = '#f0b44c', count = 22, trigger = 0, spread = 1 }: { color?: string; count?: number; trigger?: number | string; spread?: number }) {
  const reduced = useReducedMotion();
  const parts = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => {
        const a = (Math.PI * 2 * i) / count + Math.random() * 0.4;
        const d = (60 + Math.random() * 90) * spread;
        return {
          ['--dx' as string]: `${Math.cos(a) * d}px`,
          ['--dy' as string]: `${Math.sin(a) * d}px`,
          ['--rot' as string]: `${Math.random() * 360}deg`,
          ['--delay' as string]: `${Math.random() * 80}ms`,
          ['--size' as string]: `${4 + Math.random() * 6}px`,
        } as CSSProperties;
      }),
    // Recomputed per trigger so each burst scatters differently.
    [count, trigger, spread],
  );
  if (reduced) return null;
  return (
    <span className="burst" key={String(trigger)} style={{ ['--burst' as string]: color }} aria-hidden="true">
      {parts.map((style, i) => (
        <i key={i} style={style} />
      ))}
    </span>
  );
}

/** Rotating light rays behind a reward. */
export function Rays({ color = '#f0b44c' }: { color?: string }) {
  const reduced = useReducedMotion();
  return <span className={`rays ${reduced ? 'rays--still' : ''}`} style={{ ['--ray' as string]: color }} aria-hidden="true" />;
}

export interface Floater {
  id: number;
  text: string;
  tone: 'dmg' | 'crit' | 'miss' | 'heal';
}

export function FloatLayer({ items }: { items: Floater[] }) {
  return (
    <span className="floats" aria-hidden="true">
      {items.map((f) => (
        <span key={f.id} className={`float float--${f.tone}`}>
          {f.text}
        </span>
      ))}
    </span>
  );
}

/** Full-screen celebration when a levelUp event arrives. */
export function LevelUpOverlay() {
  const { lastEvents } = useSnapshot();
  const t = useT();
  const [shown, setShown] = useState<{ from: number; to: number } | null>(null);
  // Keyed on the event batch itself: other store updates (ticks, chapter
  // loads) keep the same array and must not replay the celebration.
  const seen = useRef(lastEvents);
  useEffect(() => {
    if (lastEvents === seen.current) return;
    seen.current = lastEvents;
    const ev = lastEvents.find((e) => e.type === 'levelUp');
    if (ev?.type === 'levelUp') {
      setShown({ from: ev.from, to: ev.to });
      play('levelup');
    }
  }, [lastEvents]);
  if (!shown) return null;
  const newSlot = unlockedTeamSlots(shown.to) > unlockedTeamSlots(shown.from);
  return (
    <Portal>
    <div className="overlay overlay--celebrate" role="dialog" aria-modal="true" aria-label={t('levelUp.title', { level: shown.to })} onClick={() => setShown(null)}>
      <div className="celebrate">
        <Rays color="#f0b44c" />
        <Burst color="#f0b44c" count={30} trigger={shown.to} spread={1.4} />
        <p className="celebrate__kicker">{t('levelUp.kicker')}</p>
        <p className="celebrate__big">{shown.to}</p>
        <p className="celebrate__title">{t('levelUp.title', { level: shown.to })}</p>
        <p className="celebrate__text">{t('levelUp.bonus')}</p>
        {newSlot && <p className="celebrate__text celebrate__text--strong">{t('levelUp.slot')}</p>}
        <button type="button" className="btn btn--primary btn--lg" autoFocus onClick={() => setShown(null)}>
          {t('common.continue')}
        </button>
      </div>
    </div>
    </Portal>
  );
}
